import React, { useEffect, useRef, useState } from "react";
import { Logo } from "./shared";
import { composeAgentBrand } from "@/lib/brandLabel";

// iter v27 r8 — Testi di default del volantino "Stima casa".
// Tutte le frasi sono editabili dall'utente (vedi VolantinoStimaModal).
// I `maxLength` proteggono il layout A5 da overflow.
export const VOLANTINO_STIMA_TEXTS = {
  // FRONTE
  front_header_label: {
    default: "La tua casa · valutata",
    maxLength: 28,
    label: "Etichetta in alto",
  },
  front_title_line1: {
    default: "Quanto vale",
    maxLength: 18,
    label: "Titolo · prima riga",
  },
  front_title_line2: {
    default: "casa tua?",
    maxLength: 18,
    label: "Titolo · seconda riga",
  },
  front_subtitle: {
    default: "La stessa valutazione di un sopralluogo professionale, senza muoverti da casa. Dati ufficiali e prezzi reali della tua zona.",
    maxLength: 180,
    label: "Sottotitolo",
    multiline: true,
  },
  front_bullet1_stat: { default: "30s", maxLength: 6, label: "Bullet 1 · numero" },
  front_bullet1_text: { default: "Stima sul telefono, subito.", maxLength: 42, label: "Bullet 1 · frase" },
  front_bullet2_stat: { default: "0 €", maxLength: 6, label: "Bullet 2 · numero" },
  front_bullet2_text: { default: "Gratis. Per sempre. Per chiunque.", maxLength: 42, label: "Bullet 2 · frase" },
  front_bullet3_stat: { default: "OMI", maxLength: 6, label: "Bullet 3 · numero" },
  front_bullet3_text: { default: "Dati ufficiali Agenzia delle Entrate.", maxLength: 42, label: "Bullet 3 · frase" },
  front_qr_caption: {
    default: "Inquadra · stima in 30 secondi",
    maxLength: 36,
    label: "Didascalia QR",
  },
  // RETRO
  back_header_label: {
    default: "Il tuo agente di fiducia",
    maxLength: 28,
    label: "Etichetta in alto (retro)",
  },
  back_quote: {
    default: "\u201COgni casa ha la sua persona. Trovarla è il mio lavoro.\u201D",
    maxLength: 130,
    label: "Citazione personale",
    multiline: true,
  },
  back_agent_role: {
    default: "Agente immobiliare",
    maxLength: 30,
    label: "Ruolo agente",
  },
  back_contacts_label: {
    default: "Contatti diretti",
    maxLength: 24,
    label: "Etichetta contatti",
  },
  back_promise_label: {
    default: "La nostra promessa",
    maxLength: 26,
    label: "Etichetta promessa",
  },
  back_promise_1: {
    default: "Nessuna chiamata insistente. Mai.",
    maxLength: 56,
    label: "Promessa · riga 1",
  },
  back_promise_2: {
    default: "Decidi tu quando e come parlarne.",
    maxLength: 56,
    label: "Promessa · riga 2",
  },
  back_promise_3: {
    default: "I tuoi dati restano tra di noi.",
    maxLength: 56,
    label: "Promessa · riga 3",
  },
};

// Helper: legge un testo dal `data.texts` (custom) o cade sul default.
const tx = (data, key) => {
  const custom = data?.texts?.[key];
  if (typeof custom === "string" && custom.trim().length > 0) return custom;
  return VOLANTINO_STIMA_TEXTS[key]?.default || "";
};

// ─── Template 6: Volantino Stima (A5 fronte/retro) ─────────────────
// Volantino postale 148×210mm, 2 pagine. Riusa colori/font del Brochure,
// niente "Pronto" — solo logo agenzia + contatti dal profilo.
//   FRONTE  → Headline grande + sottotitolo + 3 bullet vantaggi + QR
//   RETRO   → Foto agente + nome + telefono/email + agenzia + tagline
export function VolantinoStima({ data, lay, col, watermark, side }) {
  // side="front"/"back" → renderizza una sola facciata (usato dal chip animato
  // nella landing). Default (undefined) → grid 1×2 con entrambe le facciate.
  if (side === "front") {
    return (
      <div data-testid="static-volantino-stima-front">
        <VolantinoStimaPage label={null} bg={col.bg}>
          <VolantinoStimaFronte data={data} lay={lay} col={col} watermark={watermark} />
        </VolantinoStimaPage>
      </div>
    );
  }
  if (side === "back") {
    return (
      <div data-testid="static-volantino-stima-back">
        <VolantinoStimaPage label={null} bg={col.bg}>
          <VolantinoStimaRetro data={data} lay={lay} col={col} watermark={watermark} />
        </VolantinoStimaPage>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="static-volantino-stima">
      <VolantinoStimaPage label="01 · Fronte" bg={col.bg}>
        <VolantinoStimaFronte data={data} lay={lay} col={col} watermark={watermark} />
      </VolantinoStimaPage>
      <VolantinoStimaPage label="02 · Retro" bg={col.bg}>
        <VolantinoStimaRetro data={data} lay={lay} col={col} watermark={watermark} />
      </VolantinoStimaPage>
    </div>
  );
}

// A5 ratio 148:210 → design fisso interno 540×766px.
// Il wrapper esterno usa aspect-ratio + un transform: scale() che riempie
// la larghezza disponibile.
const VS_DESIGN_W = 540;
const VS_DESIGN_H = 766;

function VolantinoStimaPage({ children, label, bg }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(() => {
      const w = wrapRef.current?.clientWidth || VS_DESIGN_W;
      setScale(w / VS_DESIGN_W);
    });
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div data-testid="brochure-page-wrap">
      <div
        ref={wrapRef}
        className="relative rounded-sm overflow-hidden"
        data-testid="brochure-page"
        style={{ aspectRatio: `${VS_DESIGN_W}/${VS_DESIGN_H}`, background: bg, boxShadow: "0 12px 30px -12px rgba(0,0,0,0.5)" }}
      >
        <div
          style={{
            width: VS_DESIGN_W,
            height: VS_DESIGN_H,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          {children}
        </div>
      </div>
      {label && (
        <div className="text-center mt-2 text-[9px] font-bold tracking-[0.22em] uppercase" style={{ color: "#7C8B6E" }}>{label}</div>
      )}
    </div>
  );
}

// FRONTE — Headline cinematografica + microbenefici + QR
function VolantinoStimaFronte({ data, lay, col }) {
  const isMod = lay.name === "Moderno";
  const isMin = lay.name === "Minimal";
  const isLux = lay.name === "Luxury";
  const isCenter = lay.align === "center"; // Luxury
  const qrUrl = data.estimateQrUrl || data.landingQrUrl;
  return (
    <div className="absolute inset-0 flex flex-col" style={{ background: col.bg, color: col.fg, fontFamily: lay.bodyFont, textAlign: isCenter ? "center" : "left" }}>
      <div style={{ height: 6, background: col.accent }} />

      {/* Header */}
      <div style={{ padding: "20px 28px 8px", display: "flex", alignItems: "flex-start", justifyContent: isCenter ? "center" : "space-between", gap: 12, flexDirection: isCenter ? "column" : "row" }}>
        {!isCenter && (
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.softFg }}>
            {tx(data, "front_header_label")}
          </div>
        )}
        <Logo data={data} lay={lay} dark={!!col.isDark} />
        {isCenter && (
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.45em", textTransform: "uppercase", color: col.softFg, marginTop: 8 }}>
            · {tx(data, "front_header_label")} ·
          </div>
        )}
      </div>

      {/* Headline */}
      <div style={{ padding: "0 28px", marginTop: isCenter ? 18 : 6 }}>
        <h1
          style={{
            fontFamily: lay.headingFont,
            ...lay.titleStyle,
            fontSize: isMod ? 60 : (isMin ? 54 : (isLux ? 56 : 64)),
            color: col.fg,
            lineHeight: 0.95,
            letterSpacing: isMod ? "-0.035em" : (isLux ? "0" : "-0.025em"),
            marginBottom: 14,
            margin: isCenter ? "0 auto 14px" : "0 0 14px",
          }}
        >
          {/* iter v27 r8 — Titolo editabile: line1 + line2. Mantenuto split
              colorato sull'ultima parola di line1 per preservare il look.
              Esempio: "Quanto vale" → "Quanto " + "vale" (accent). */}
          {(() => {
            const line1 = tx(data, "front_title_line1");
            const line2 = tx(data, "front_title_line2");
            const upper = isMod;
            const words = line1.split(" ");
            const lastWord = words.pop();
            const firstPart = words.length ? words.join(" ") + " " : "";
            return (
              <>
                {upper ? firstPart.toUpperCase() : firstPart}
                <span style={{ fontStyle: lay.name === "Elegante" || isLux ? "italic" : "normal", color: col.accent }}>
                  {upper ? lastWord.toUpperCase() : lastWord}
                </span><br/>
                {upper ? line2.toUpperCase() : line2}
              </>
            );
          })()}
        </h1>
        {isLux && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 14 }}>
            <span style={{ flex: 1, height: 1, background: col.border }} />
            <span style={{ fontSize: 8, letterSpacing: "0.4em", textTransform: "uppercase", color: col.accent, fontWeight: 700 }}>· · ·</span>
            <span style={{ flex: 1, height: 1, background: col.border }} />
          </div>
        )}
        <p style={{ fontSize: 15, lineHeight: 1.35, color: col.softFg, margin: isCenter ? "0 auto" : "0", maxWidth: isCenter ? 400 : "none" }}>
          {tx(data, "front_subtitle")}
        </p>
      </div>

      {/* Spacer 1 */}
      <div style={{ flex: 1 }} />

      {/* Bullets bilanciati */}
      <ul style={{ padding: "0 28px", listStyle: "none", display: "flex", flexDirection: "column", gap: 8, maxWidth: 340, margin: "0 auto", width: "100%" }}>
        {[
          { n: tx(data, "front_bullet1_stat"), t: tx(data, "front_bullet1_text") },
          { n: tx(data, "front_bullet2_stat"), t: tx(data, "front_bullet2_text") },
          { n: tx(data, "front_bullet3_stat"), t: tx(data, "front_bullet3_text") },
        ].map((b, i) => (
          <li key={i} style={{ display: "flex", alignItems: "baseline", gap: 14, borderBottom: `1px solid ${col.border}`, paddingBottom: 7 }}>
            <div style={{ fontFamily: lay.headingFont, ...lay.titleStyle, fontSize: 22, color: col.accent, minWidth: 56, letterSpacing: isLux ? "0.04em" : "-0.02em", textAlign: "left" }}>{b.n}</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: col.fg, lineHeight: 1.3, flex: 1, textAlign: "left" }}>{b.t}</div>
          </li>
        ))}
      </ul>

      {/* Spacer 2 */}
      <div style={{ flex: 1 }} />

      {/* QR grande centrale */}
      <div style={{ padding: "0 28px 26px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.32em", textTransform: "uppercase", color: col.softFg }}>
          {tx(data, "front_qr_caption")}
        </div>
        <div style={{ background: "#fff", padding: 10, borderRadius: 4, border: `1px solid ${col.border}` }}>
          {qrUrl ? (
            <img src={qrUrl} alt="Inquadra per la stima" style={{ width: 150, height: 150, display: "block" }} />
          ) : (
            <div style={{ width: 150, height: 150, background: `repeating-linear-gradient(45deg, ${col.fg}, ${col.fg} 3px, #fff 3px, #fff 6px)` }} />
          )}
        </div>
        <div style={{ fontSize: 12, color: col.softFg, textAlign: "center", lineHeight: 1.3 }}>
          oppure chiama <strong style={{ color: col.fg, whiteSpace: "nowrap" }}>{data.agentPhone}</strong>
        </div>
      </div>

      <div style={{ height: 6, background: col.accent }} />
    </div>
  );
}

// RETRO — Tagline + foto agente + scheda contatti
function VolantinoStimaRetro({ data, lay, col }) {
  const isMod = lay.name === "Moderno";
  const isMin = lay.name === "Minimal";
  const isLux = lay.name === "Luxury";
  return (
    <div className="absolute inset-0 flex flex-col" style={{ background: col.bg, color: col.fg, fontFamily: lay.bodyFont }}>
      <div style={{ height: 6, background: col.accent }} />

      {/* Header: tagline + logo agenzia */}
      <div style={{ padding: "20px 28px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.softFg }}>
          {tx(data, "back_header_label")}
        </div>
        {/* iter 275 round 40: dark prop derivato dal theme (col.isDark). */}
        <Logo data={data} lay={lay} dark={!!col.isDark} />
      </div>

      {/* Sezione tagline grande · iter v27 r8: citazione editabile */}
      <div style={{ padding: "0 28px", marginTop: 28 }}>
        <blockquote
          style={{
            fontFamily: lay.headingFont,
            ...lay.titleStyle,
            fontSize: isMod ? 30 : (isMin ? 26 : 32),
            color: col.fg,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          {isMod ? tx(data, "back_quote").toUpperCase() : tx(data, "back_quote")}
        </blockquote>
      </div>

      {/* Card agente */}
      <div style={{ flex: 1 }} />

      <div style={{ padding: "0 28px", marginTop: 0, display: "flex", alignItems: "center", gap: 14 }}>
        {/* iter v28 r368 — User M1278: gerarchia avatar nel cerchio retro:
              1. Foto profilo agente (agent_photo) — preferita
              2. Logo agenzia (agency_logo_url) — fallback
              3. Iniziali del nome agente/agenzia — ultimo fallback */}
        {data.agentPhoto ? (
          // iter v29 r170 — User M1503: «la foto profilo nel volantino
          // stima esce sgranata». Causa: usavamo backgroundImage su <div>,
          // html2canvas la rasterizza con scaling nearest-neighbor e su
          // export 300 DPI il viso appare granuloso.
          // Fix: usiamo <img> reale dentro un wrapper circolare con
          // overflow:hidden. html2canvas tratta <img> con interpolazione
          // bicubica nativa del browser → viso netto anche a 1748px.
          // Inoltre marchiamo l'<img> con data-hires-export così la
          // pipeline di pre-processing (recolorBlackAndWhiteImages) la
          // upscale a min 800px lato lungo PRIMA della cattura.
          <div
            style={{
              width: 78,
              height: 78,
              borderRadius: "50%",
              overflow: "hidden",
              border: `2px solid ${col.accent}`,
              flexShrink: 0,
              position: "relative",
            }}
            role="img"
            aria-label={data.agentName}
          >
            <img
              src={data.agentPhoto}
              alt={data.agentName}
              crossOrigin="anonymous"
              data-hires-export="800"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 25%",
                display: "block",
              }}
            />
          </div>
        ) : data.agencyLogoUrl ? (
          // Fallback livello 2: logo agenzia.
          // Sfondo bianco con padding così il logo (di solito su trasparenza)
          // resta leggibile. Il logo viene forzato in B/W coerentemente con
          // il resto dei materiali editoriali Pronto (vedi shared.jsx).
          <div
            style={{
              width: 78, height: 78, borderRadius: "50%",
              background: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `2px solid ${col.accent}`,
              flexShrink: 0,
              padding: 8,
              boxSizing: "border-box",
            }}
          >
            <img
              src={data.agencyLogoBwBlackUrl || data.agencyLogoUrl}
              alt={data.agencyName || ""}
              crossOrigin="anonymous"
              style={{
                maxWidth: "100%", maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </div>
        ) : (
          // Fallback livello 3: iniziali. Preferenza al nome agente (più
          // personale), poi nome agenzia, infine "A".
          <div
            style={{
              width: 78, height: 78, borderRadius: "50%",
              background: col.accent, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: lay.headingFont, fontSize: 32, fontStyle: "italic",
              border: `2px solid ${col.accent}`,
              flexShrink: 0,
            }}
          >
            {(data.agentName || data.agencyName || "A").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          {/* iter v29 r201 — Anti-duplicazione nome agente.
              Se "Luca Ferrari" è dentro "Casa Bella di Luca Ferrari" non lo
              ripetiamo: composeAgentBrand ritorna primary=agency e secondary=null.
              Comportamento storico preservato per nomi davvero diversi. */}
          {(() => {
            const { primary, secondary, includesAgent } = composeAgentBrand(data.agentName, data.agencyName);
            if (secondary) {
              return (
                <>
                  <div style={{ fontFamily: lay.headingFont, ...lay.titleStyle, fontSize: 24, color: col.fg, letterSpacing: "-0.015em", lineHeight: 1.05 }}>
                    {primary}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, marginTop: 3, color: col.softFg }}>
                    {tx(data, "back_agent_role")} · {secondary}
                  </div>
                </>
              );
            }
            return (
              <>
                <div style={{ fontFamily: lay.headingFont, ...lay.titleStyle, fontSize: 24, color: col.fg, letterSpacing: "-0.015em", lineHeight: 1.05 }}>
                  {primary}
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, marginTop: 3, color: col.softFg }}>
                  {tx(data, "back_agent_role")}
                </div>
              </>
            );
          })()}
          {data.agentLicense && (
            <div style={{ fontSize: 11, marginTop: 2, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, color: col.accent }}>
              {data.agentLicense}
            </div>
          )}
        </div>
      </div>

      {/* Scheda contatti — iter v28r10: ogni riga è opzionale.
          Mostriamo solo i campi compilati dall'agente. Sede e Sito ora
          sono righe separate (prima erano collassate in fallback). */}
      <div style={{ padding: "0 28px", marginTop: 22 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.accent, marginBottom: 10 }}>
          {tx(data, "back_contacts_label")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[
            { l: "Tel", v: data.agentPhone },
            { l: "Email", v: data.agentEmail },
            { l: "Sede", v: data.agencyAddress },
            { l: "Sito", v: data.agencyWeb },
          ]
            .filter((row) => row.v && String(row.v).trim())
            .map((row, i) => (
              <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 14, borderBottom: `1px solid ${col.border}`, paddingBottom: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.20em", textTransform: "uppercase", color: col.softFg, minWidth: 42 }}>{row.l}</div>
                <div style={{ fontSize: 13, fontWeight: 600, flex: 1, color: col.fg, wordBreak: "break-all" }}>{row.v}</div>
              </div>
            ))}
        </div>
      </div>

      {/* Spacer prima del footer */}
      <div style={{ flex: 1 }} />

      {/* Footer: la nostra promessa */}
      <div style={{ padding: "0 28px 24px" }}>
        <div
          style={{
            borderRadius: 4,
            padding: "14px 16px",
            background: `${col.accent}10`,
            border: `1px solid ${col.border}`,
          }}
        >
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.30em", textTransform: "uppercase", color: col.accent, marginBottom: 8 }}>
            {tx(data, "back_promise_label")}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 4 }}>
            <li style={{ fontSize: 13, lineHeight: 1.35, color: col.fg }}>· {tx(data, "back_promise_1")}</li>
            <li style={{ fontSize: 13, lineHeight: 1.35, color: col.fg }}>· {tx(data, "back_promise_2")}</li>
            <li style={{ fontSize: 13, lineHeight: 1.35, color: col.fg }}>· {tx(data, "back_promise_3")}</li>
          </ul>
        </div>
      </div>

      <div style={{ height: 6, background: col.accent }} />
    </div>
  );
}
